import { useMemo } from "react";
import { Badge, Box, Group, Tooltip, Text } from "@mantine/core";
import {
  IconCircleCheckFilled,
  IconDotsCircleHorizontal,
} from "@tabler/icons-react";
import { PrintSlideButton } from "../../../components/workflows/PrintSlideButton";
import {
  PrintStickerButton,
  ReceivedExternalBadge,
} from "../../../components/sticker";
import { DeleteStainButton } from "../../../components/workflows";

interface Props {
  slide: any;
  label: string;
  caseId: string;
  caseNumber?: string;
  /** Case signed off / blocked, or this specimen already signed. */
  isReadOnly?: boolean;
  allowPostSignOffEvaluation?: boolean;
}

export function SlidePill({
  slide,
  label,
  caseId,
  caseNumber,
  isReadOnly,
  allowPostSignOffEvaluation,
}: Props) {
  const isCompleted = slide.isCompleted;
  const stainCode = useMemo(() => slide.stain?.code || "H&E", [slide.stain]);
  const isExternal = useMemo(
    () =>
      slide.origin === "sending_client" || slide.origin === "external_provider",
    [slide.origin]
  );
  const isPreStained = slide.origin === "sending_client";

  return (
    <Box
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        background: isPreStained
          ? "var(--mantine-color-cyan-0)"
          : "var(--mantine-color-gray-1)",
        border: isPreStained
          ? "1px solid var(--mantine-color-cyan-3)"
          : "1px solid var(--mantine-color-gray-3)",
        fontSize: 11,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <span>{label}</span>
      <span
        style={{
          color: "var(--mantine-color-gray-5)",
          fontWeight: 400,
        }}
      >
        {stainCode}
        {slide.stain?.cptCode?.code && (
          <span
            style={{
              color: "var(--mantine-color-blue-6)",
              marginLeft: 4,
              fontSize: 10,
              fontWeight: 600,
            }}
          >
            [{slide.stain.cptCode.code}]
          </span>
        )}
      </span>
      {isPreStained && (
        <Tooltip
          label="Received already stained from the referring lab — no in-house staining performed."
          withArrow
          multiline
          maw={240}
        >
          <Badge size="xs" variant="light" color="cyan">
            Pre-stained
          </Badge>
        </Tooltip>
      )}
      {slide.insufficientTissue === true && (
        <Text span size="xs" c="red" fw={600}>
          Insufficient tissue
        </Text>
      )}
      <Group gap={4} wrap="nowrap">
        {isExternal && isCompleted && (
          <ReceivedExternalBadge
            compact
            isReprinted={!!slide.lastStickerPrintedAt}
            lastStickerPrintedAt={slide.lastStickerPrintedAt}
          />
        )}
        <PrintStickerButton
          caseId={caseId}
          itemId={slide.id}
          itemType="slide"
          renderedSlide={slide}
        />
        <PrintSlideButton caseId={caseId} slide={slide} disabled={isExternal} />
        <DeleteStainButton
          slide={slide}
          caseId={caseId}
          caseNumber={caseNumber}
          slideLabel={label}
          disabled={isReadOnly}
          allowPostSignOffEvaluation={allowPostSignOffEvaluation}
          evaluateEligibility
        />
      </Group>
      {isCompleted ? (
        <IconCircleCheckFilled size={14} color="var(--mantine-color-green-5)" />
      ) : (
        <IconDotsCircleHorizontal
          size={14}
          color="var(--mantine-color-orange-5)"
        />
      )}
    </Box>
  );
}
